import { useRef, useState, useEffect } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import {
    BedDouble,
    Activity,
    BarChart2,
    UserPlus,
    LogOut,
    Briefcase,
    FileText,
    Settings
} from 'lucide-react';
import UserAvatar from './UserAvatar';
import api from '../services/api';

const dockItems = [
    { id: "beds", name: "Leitos", icon: <BedDouble size={28} />, path: "/", color: "bg-blue-500" },
    { id: "status", name: "Status", icon: <Activity size={28} />, path: "/status", color: "bg-indigo-500" },
    { id: "metrics", name: "Métricas", icon: <BarChart2 size={28} />, path: "/metrics", color: "bg-purple-500" },
    { id: "checkin", name: "Check-in", icon: <UserPlus size={28} />, path: "/checkin", color: "bg-green-600" },
    { id: "checkout", name: "Check-out", icon: <LogOut size={28} />, path: "/checkout", color: "bg-red-500" },
    { id: "services", name: "Serviços", icon: <Briefcase size={28} />, path: "/services", color: "bg-orange-500" },
    { id: "logs", name: "Logs", icon: <FileText size={28} />, path: "/audit-logs", color: "bg-gray-600" },
    { id: "settings", name: "Configurações", icon: <Settings size={28} />, path: "/settings", color: "bg-slate-700" },
];

function NavIcon({ item, mouseX }) {
    const ref = useRef(null);
    const location = useLocation();
    const isActive = location.pathname === item.path;

    const distance = useTransform(mouseX, (val) => {
        const bounds = ref.current?.getBoundingClientRect() ?? { x: 0, width: 0 };
        return val - bounds.x - bounds.width / 2;
    });

    const widthSync = useTransform(distance, [-150, 0, 150], [55, 85, 55]); // Base 55, Grows to 85
    const width = useSpring(widthSync, { mass: 0.1, stiffness: 150, damping: 12 });

    const heightSync = useTransform(distance, [-150, 0, 150], [55, 85, 55]);
    const height = useSpring(heightSync, { mass: 0.1, stiffness: 150, damping: 12 });

    const [isHovered, setIsHovered] = useState(false);

    return (
        <motion.div
            ref={ref}
            style={{ width, height, zIndex: isHovered ? 100 : 10 }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            className="aspect-square cursor-pointer flex items-center justify-center relative group mx-1"
        >
            <Link to={item.path} className="w-full h-full flex items-center justify-center">
                <motion.div
                    className={`w-full h-full rounded-2xl shadow-md flex items-center justify-center text-white relative overflow-hidden ${item.color}`}
                    animate={{
                        y: isActive ? 5 : isHovered ? 2 : 0,
                    }}
                    whileTap={{ scale: 0.9 }}
                    transition={{
                        type: "spring",
                        stiffness: 300,
                        damping: 20,
                    }}
                >
                    <motion.div
                        animate={{
                            scale: isHovered || isActive ? 1.1 : 1,
                        }}
                    >
                        {item.icon}
                    </motion.div>

                    {/* Shine effect */}
                    <motion.div
                        className="absolute inset-0 bg-gradient-to-br from-white/30 to-transparent pointer-events-none"
                        animate={{
                            opacity: isHovered ? 0.4 : 0.1,
                        }}
                    />
                </motion.div>
            </Link>

            {/* Tooltip (Label) - BELOW the icon */}
            <motion.div
                initial={{ opacity: 0, y: -5, scale: 0.8 }}
                animate={{
                    opacity: isHovered ? 1 : 0,
                    y: isHovered ? 60 : 0, // Increased distance slightly to ensure it clears the icon 
                    scale: isHovered ? 1 : 0.8,
                }}
                className="absolute top-0 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-sm font-medium px-3 py-1.5 rounded-lg whitespace-nowrap pointer-events-none shadow-xl border border-gray-700/50"
            >
                {item.name}
            </motion.div>

            {/* Active Indicator Dot - Top for top navbar */}
            {isActive && (
                <motion.div
                    layoutId="active-dot"
                    className="absolute -top-3 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-blue-500 rounded-full shadow-[0_0_8px_rgba(59,130,246,0.6)]"
                />
            )}
        </motion.div>
    );
}

function HealthIndicator() {
    const [status, setStatus] = useState({ online: false, mode: 'unknown' });

    // Poll a cada 30 segundos
    useEffect(() => {
        const checkHealth = async () => {
            try {
                const response = await api.get('/health'); // Uses axios instance
                setStatus({
                    online: response.data.status === 'online',
                    mode: response.data.mode // 'supabase' or 'memory'
                });
            } catch (error) {
                setStatus({ online: false, mode: 'offline' });
            }
        };

        checkHealth();
        const interval = setInterval(checkHealth, 30000);
        return () => clearInterval(interval);
    }, []);

    const getColor = () => {
        if (!status.online) return 'bg-red-500';
        if (status.mode === 'memory') return 'bg-yellow-500'; // Online mas sem Supabase
        return 'bg-green-500'; // Supabase conectado
    };

    const getTitle = () => {
        if (!status.online) return 'Sistema Offline (Sem Backend)';
        if (status.mode === 'memory') return 'Modo Offline (Dados em Memória)';
        return 'Sistema Online (Conectado)';
    };

    return (
        <div className="relative group flex items-center justify-center">
            <div className={`w-3 h-3 rounded-full ${getColor()} shadow-sm ring-2 ring-white cursor-help transition-colors duration-500`}></div>

            {/* Tooltip */}
            <div className="absolute top-8 left-1/2 -translate-x-1/2 w-max px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                {getTitle()}
            </div>
        </div>
    );
}

export function TopNavigation() {
    const mouseX = useMotionValue(Infinity);

    return (
        <div className="fixed top-6 left-0 right-0 z-50 hidden md:flex items-start justify-center pointer-events-none">
            <div className="pointer-events-auto flex items-center bg-white/80 backdrop-blur-xl rounded-full px-6 py-3 border border-gray-200/50 shadow-2xl ring-1 ring-black/5">
                {/* Logo Section */}
                <div className="mr-8 flex items-center gap-3 pr-8 border-r border-gray-300/50 h-10">
                    <span className="text-2xl drop-shadow-sm">🏥</span>
                    <span className="font-bold text-gray-800 hidden md:block text-lg">Gestão Leitos</span>
                </div>

                {/* Animated Icons */}
                <motion.div
                    onMouseMove={(e) => mouseX.set(e.pageX)}
                    onMouseLeave={() => mouseX.set(Infinity)}
                    className="flex items-center gap-3 h-20"
                >
                    {dockItems.map((item) => (
                        <NavIcon key={item.id} item={item} mouseX={mouseX} />
                    ))}
                </motion.div>

                {/* User Avatar Section with Health Indicator */}
                <div className="ml-8 pl-8 border-l border-gray-300/50 h-10 flex items-center gap-3">
                    <HealthIndicator />
                    <UserAvatar />
                </div>
            </div>
        </div>
    );
}
